import { useEffect, useState } from 'react';
import type { IPersonListItem } from '@skystone/shared';
import { personApi } from '@/services/person-api';
import { getErrorMessage } from '@/lib/utils';
import { Dialog, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { KindBadge } from './KindBadge';
import type { ReconcileTarget } from './reconcile-target';

interface LinkToPersonDialogProps {
  open: boolean;
  target: ReconcileTarget | null;
  onClose: () => void;
  onConfirmed: () => void;
}

function titleFor(target: ReconcileTarget | null): string {
  if (target?.source === 'github') return 'Link GitHub account to Person';
  return 'Link Slack account to Person';
}

async function linkForSource(
  target: ReconcileTarget,
  personId: string,
): Promise<unknown> {
  if (target.source === 'github') {
    return personApi.linkGitHub({ login: target.sourceAccountId, personId });
  }
  return personApi.linkSlack({ slackId: target.sourceAccountId, personId });
}

export function LinkToPersonDialog({
  open,
  target,
  onClose,
  onConfirmed,
}: Readonly<LinkToPersonDialogProps>) {
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState<IPersonListItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<IPersonListItem | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setCandidates([]);
      setSelected(null);
      setError(null);
    }
  }, [open]);

  const runSearch = async (value: string): Promise<void> => {
    setSearching(true);
    setError(null);
    try {
      const response = await personApi.list({
        search: value || undefined,
        pageSize: 20,
      });
      setCandidates(response.items);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Search failed'));
    } finally {
      setSearching(false);
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    runSearch(query.trim());
  };

  const handleConfirm = async (): Promise<void> => {
    if (!target || !selected) return;
    setSubmitting(true);
    setError(null);
    try {
      await linkForSource(target, selected.id);
      onConfirmed();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to link'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title={titleFor(target)}>
      {target && (
        <div className="mb-3 rounded-md border bg-muted/40 p-3 text-sm">
          <div className="font-medium">{target.displayName}</div>
          <div className="text-xs text-muted-foreground">
            {target.email ?? '—'} · {target.sourceAccountId}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="mb-3 flex gap-2">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name or email…"
          className="flex-1 rounded-md border bg-background px-3 py-2 text-sm"
        />
        <Button type="submit" size="sm" isLoading={searching}>Search</Button>
      </form>

      {error && <p className="mb-2 text-sm text-error-700">{error}</p>}

      <div className="max-h-64 overflow-auto rounded-md border">
        {candidates.length === 0 && !searching && (
          <p className="p-3 text-sm text-muted-foreground">
            Search by name or email to pick a Person.
          </p>
        )}
        <ul className="divide-y">
          {candidates.map((person) => (
            <li key={person.id}>
              <button
                type="button"
                onClick={() => setSelected(person)}
                className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-muted/50 ${
                  selected?.id === person.id ? 'bg-muted' : ''
                }`}
              >
                <div className="min-w-0">
                  <div className="truncate font-medium">{person.displayName}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {person.primaryEmail ?? '—'}
                  </div>
                </div>
                <KindBadge kind={person.kind} />
              </button>
            </li>
          ))}
        </ul>
      </div>

      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button onClick={handleConfirm} isLoading={submitting} disabled={!selected || submitting}>
          Link
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
